// Public pixel ID. The Conversions API token belongs only in server settings.
export const BLISS_META_PIXEL_ID = '1471223131517283';
const KEY = 'bliss_meta_measurement_consent_v1';
type Pixel = ((...args: unknown[]) => void) & { queue?: unknown[][]; callMethod?: (...args: unknown[]) => void; push?: Pixel; loaded?: boolean; version?: string };
type MetaWindow = Window & { fbq?: Pixel; _fbq?: Pixel };
let initialized = false;
let consent = true;
const measured = new Set<string>();
export function hasMetaMeasurementConsent(): boolean {
  if (typeof window === 'undefined' || window.location.hostname !== 'design.blissbunkbeds.com') return false;
  if (isMeasurementTestSession()) return false;
  if ((navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl || navigator.doNotTrack === '1') return false;
  try { return sessionStorage.getItem(KEY) !== 'no'; } catch { return consent; }
}
export function setMetaMeasurementConsent(value: boolean): void {
  consent = value;
  try { sessionStorage.setItem(KEY, value ? 'yes' : 'no'); } catch { /* optional storage */ }
  try {
    if (!hasMetaMeasurementConsent()) { (window as MetaWindow).fbq?.('consent', 'revoke'); return; }
    initializeMetaMeasurement();
  } catch { /* Measurement never blocks the form. */ }
}
export function initializeMetaMeasurement(): void {
  if (!hasMetaMeasurementConsent()) return;
  const win = window as MetaWindow;
  if (!win.fbq) {
    const fbq: Pixel = (...args) => { if (fbq.callMethod) fbq.callMethod(...args); else fbq.queue!.push(args); };
    fbq.queue = []; fbq.push = fbq; fbq.loaded = true; fbq.version = '2.0';
    win.fbq = fbq; win._fbq = fbq;
  }
  win.fbq('consent', 'grant');
  if (initialized) return;
  // Disable automatic button/form event collection; send explicit successful actions only.
  win.fbq('set', 'autoConfig', false, BLISS_META_PIXEL_ID);
  win.fbq('init', BLISS_META_PIXEL_ID);
  if (!document.querySelector('script[src="https://connect.facebook.net/en_US/fbevents.js"]')) {
    const script = document.createElement('script'); script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js'; document.head.appendChild(script);
  }
  win.fbq('trackSingle', BLISS_META_PIXEL_ID, 'PageView');
  initialized = true;
}
export function metaMeasurementContext() {
  const granted = hasMetaMeasurementConsent();
  return { enabled: granted, version: 2, basis: 'opt_out', test: isMeasurementTestSession(), ...(granted ? { userAgent: navigator.userAgent.slice(0, 500) } : {}) };
}
/** Called only after a new inquiry is saved successfully by the server. */
export function measureMetaSavedInquiry(id: string): void {
  try {
    if (!hasMetaMeasurementConsent() || !id) return;
    const key = `bliss_meta_lead:${id}`;
    if (measured.has(key)) return;
    try { if (sessionStorage.getItem(key)) return; } catch { /* optional */ }
    initializeMetaMeasurement();
    (window as MetaWindow).fbq?.('trackSingle', BLISS_META_PIXEL_ID, 'Lead', {}, { eventID: `bliss_lead_${id}` });
    measured.add(key);
    try { sessionStorage.setItem(key, '1'); } catch { /* optional */ }
  } catch { /* A blocked tag must not fail a saved inquiry. */ }
}

/** Explicit QA mode persists for this tab; never send test traffic to ad optimization. */
let measurementTest = false;
export function isMeasurementTestSession(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get('measurement_test') === '1') {
      measurementTest = true;
      try { sessionStorage.setItem('bliss_measurement_test', '1'); } catch { /* optional */ }
      return true;
    }
    return measurementTest || sessionStorage.getItem('bliss_measurement_test') === '1';
  } catch { return measurementTest; }
}
export const PREVIEW_EVENTS = [
  'landing_view', 'preview_start', 'photo_selected', 'photo_loaded', 'upload_failed',
  'clearing_started', 'clearing_complete', 'clearing_failed', 'alignment_failed',
  'preview_ready', 'render_failed', 'price_view', 'contact_view', 'inquiry_saved',
] as const;
export type PreviewEvent = typeof PREVIEW_EVENTS[number];
const previewEvents = new Set<string>();
/** One event per stage per tab session, so retries/rerenders do not inflate stage reach.
 * No photos, filenames, error messages, contact details or room data are transmitted.
 * Counts cover consenting, unblocked sessions only; they are not all website visitors.
 */
export function measurePreviewEvent(event: PreviewEvent): void {
  try {
    if (!PREVIEW_EVENTS.includes(event) || !hasMetaMeasurementConsent()) return;
    const key = 'bliss_preview_v1:' + event;
    if (previewEvents.has(key)) return;
    try { if (sessionStorage.getItem(key)) return; } catch { /* optional */ }
    initializeMetaMeasurement();
    (window as MetaWindow).fbq?.('trackSingleCustom', BLISS_META_PIXEL_ID, 'bliss_' + event, { funnel_version: 'preview_v1' });
    previewEvents.add(key);
    try { sessionStorage.setItem(key, '1'); } catch { /* optional */ }
  } catch { /* Measurement must never interrupt a customer's room. */ }
}

